import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { doc, getDoc, updateDoc, collection, addDoc, deleteDoc, setDoc } from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import {
  Edit3,
  Save,
  X,
  Plus,
  Upload,
  Calendar,
  Globe,
  MapPin,
  Phone,
  Mail,
  Building2,
  DollarSign,
  Users,
} from 'lucide-react';
import ClientForm from './ClientForm';
import ProjectForm from './ProjectForm';
import StageSelector from '../ui/StageSelector';
import { toast } from 'react-toastify';

export default function CompanyProfileManager({ organizationId, isOwner = false }) {
  const [profileData, setProfileData] = useState({
    name: '',
    profileLink: '',
    launchDate: '',
    isLaunched: false,
    description: '',
    location: '',
    phone: '',
    email: '',
    website: '',
    photos: [],
    videos: [],
    projects: [],
    clients: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const [showClientForm, setShowClientForm] = useState(false);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editingProject, setEditingProject] = useState(null);

  const [clientFields, setClientFields] = useState({});
  const [newFieldKey, setNewFieldKey] = useState('');
  const [newFieldValue, setNewFieldValue] = useState('');

  useEffect(() => {
    loadProfileData();
  }, [organizationId]);

  const loadProfileData = async () => {
    try {
      const profileDoc = await getDoc(doc(firestore, 'companyProfiles', organizationId));
      if (profileDoc.exists()) setProfileData(prev => ({ ...prev, ...profileDoc.data() }));

      const clientDoc = await getDoc(doc(firestore, 'clients', organizationId));
      if (clientDoc.exists()) {
        const data = clientDoc.data();
        setClientFields(data || {});
        setProfileData(prev => ({
          ...prev,
          name: data?.organization || prev.name || '',
          location: data?.location || prev.location || '',
          phone: data?.phone || prev.phone || '',
          email: data?.email || prev.email || '',
        }));
      } else {
        setClientFields({});
      }
    } catch (error) {
      toast.error('Error loading profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveProfileData = async () => {
    setSaving(true);
    try {
      await setDoc(doc(firestore, 'companyProfiles', organizationId), profileData, { merge: true });

      if (clientFields && typeof clientFields === 'object') {
        await setDoc(doc(firestore, 'clients', organizationId), clientFields, { merge: true });
      }
      setIsEditing(false);
    } catch (error) {
      toast.error('Error saving profile data:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleClientFieldChange = (key, value) => {
    setClientFields(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const addNewClientField = () => {
    const key = newFieldKey?.trim();
    if (!key) return;
    setClientFields(prev => ({ ...prev, [key]: newFieldValue }));
    setNewFieldKey('');
    setNewFieldValue('');
  };

  const removeClientField = key => {
    setClientFields(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const addPhoto = async photoData => {
    try {
      const photoRef = await addDoc(
        collection(firestore, 'companyProfiles', organizationId, 'photos'),
        photoData
      );
      setProfileData(prev => ({
        ...prev,
        photos: [...prev.photos, { id: photoRef.id, ...photoData }],
      }));
    } catch (error) {
      toast.error('Error adding photo:', error);
    }
  };

  const addVideo = async videoData => {
    try {
      const videoRef = await addDoc(
        collection(firestore, 'companyProfiles', organizationId, 'videos'),
        videoData
      );
      setProfileData(prev => ({
        ...prev,
        videos: [...prev.videos, { id: videoRef.id, ...videoData }],
      }));
    } catch (error) {
      toast.error('Error adding video:', error);
    }
  };

  const addProject = async projectData => {
    try {
      const projectRef = await addDoc(
        collection(firestore, 'companyProfiles', organizationId, 'projects'),
        projectData
      );
      setProfileData(prev => ({
        ...prev,
        projects: [...prev.projects, { id: projectRef.id, ...projectData }],
      }));
    } catch (error) {
      toast.error('Error adding project:', error);
    }
  };

  const addClient = async clientData => {
    try {
      const clientRef = await addDoc(
        collection(firestore, 'companyProfiles', organizationId, 'clients'),
        clientData
      );
      setProfileData(prev => ({
        ...prev,
        clients: [...prev.clients, { id: clientRef.id, ...clientData }],
      }));
    } catch (error) {
      toast.error('Error adding client:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value,
    }));

    if (field === 'name') {
      setClientFields(prev => ({ ...prev, organization: value }));
    }
    if (field === 'location') {
      setClientFields(prev => ({ ...prev, location: value }));
    }
    if (field === 'phone') {
      setClientFields(prev => ({ ...prev, phone: value }));
    }
    if (field === 'email') {
      setClientFields(prev => ({ ...prev, email: value }));
    }
  };

  const handleClientAdded = newClient => {
    setProfileData(prev => ({
      ...prev,
      clients: [...prev.clients, newClient],
    }));
    setShowClientForm(false);
    setEditingClient(null);
  };

  const handleProjectAdded = newProject => {
    setProfileData(prev => ({
      ...prev,
      projects: [...prev.projects, newProject],
    }));
    setShowProjectForm(false);
    setEditingProject(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="h-8 w-8 rounded-full border-4 border-slate-300 border-t-slate-600"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border bg-white p-4 shadow-sm md:p-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900">Company Profile Manager</h2>
            <p className="mt-1 text-slate-600">
              Manage your company's public profile and information here
            </p>
          </div>
          {isOwner && (
            <button
              onClick={() => (isEditing ? saveProfileData() : setIsEditing(true))}
              disabled={saving}
              className="flex items-center space-x-2 rounded-xl bg-slate-900 px-4 py-2 text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {isEditing ? (
                <>
                  <Save className="h-4 w-4" />
                  <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                </>
              ) : (
                <>
                  <Edit3 className="h-4 w-4" />
                  <span>Edit Profile</span>
                </>
              )}
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">Company Name</label>
            <input
              type="text"
              value={profileData.name}
              onChange={e => handleInputChange('name', e.target.value)}
              disabled={!isEditing}
              placeholder="Organization (from clients)"
              className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600 disabled:bg-slate-50"
            />
            <p className="mt-1 text-xs text-slate-500">Maps to clients.organization</p>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">Profile Link</label>
            <input
              type="url"
              value={profileData.profileLink}
              onChange={e => handleInputChange('profileLink', e.target.value)}
              disabled={!isEditing}
              className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600 disabled:bg-slate-50"
              placeholder="https://example.com/company-profile"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">Launch Date</label>
            <input
              type="date"
              value={profileData.launchDate}
              onChange={e => handleInputChange('launchDate', e.target.value)}
              disabled={!isEditing}
              className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600 disabled:bg-slate-50"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">Launch Status</label>
            <select
              value={profileData.isLaunched ? 'launched' : 'not-launched'}
              onChange={e => handleInputChange('isLaunched', e.target.value === 'launched')}
              disabled={!isEditing}
              className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600 disabled:bg-slate-50"
            >
              <option value="not-launched">Not Launched</option>
              <option value="launched">Launched</option>
            </select>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">Location</label>
            <input
              type="text"
              value={profileData.location}
              onChange={e => handleInputChange('location', e.target.value)}
              disabled={!isEditing}
              className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600 disabled:bg-slate-50"
              placeholder="City, Country"
            />
            <p className="mt-1 text-xs text-slate-500">Maps to clients.location</p>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">Phone</label>
            <input
              type="tel"
              value={profileData.phone}
              onChange={e => handleInputChange('phone', e.target.value)}
              disabled={!isEditing}
              className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600 disabled:bg-slate-50"
            />
            <p className="mt-1 text-xs text-slate-500">Maps to clients.phone</p>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">Email</label>
            <input
              type="email"
              value={profileData.email}
              onChange={e => handleInputChange('email', e.target.value)}
              disabled={!isEditing}
              className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600 disabled:bg-slate-50"
            />
            <p className="mt-1 text-xs text-slate-500">Maps to clients.email</p>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">Website</label>
            <input
              type="url"
              value={profileData.website}
              onChange={e => handleInputChange('website', e.target.value)}
              disabled={!isEditing}
              className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600 disabled:bg-slate-50"
              placeholder="https://company.com"
            />
          </div>
        </div>

        <div className="mt-6">
          <label className="mb-2 block text-sm font-medium text-slate-700">
            Company Description
          </label>
          <textarea
            value={profileData.description}
            onChange={e => handleInputChange('description', e.target.value)}
            disabled={!isEditing}
            rows={4}
            className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600 disabled:bg-slate-50"
            placeholder="Describe your company, mission, and values..."
          />
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="mb-2 block text-sm font-medium text-slate-700">Business Stage</label>
            <StageSelector
              value={clientFields.businessStage || ''}
              onChange={value => handleClientFieldChange('businessStage', value)}
              disabled={!isEditing}
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">Status</label>
            <select
              value={clientFields.status || ''}
              onChange={e => handleClientFieldChange('status', e.target.value)}
              disabled={!isEditing}
              className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600 disabled:bg-slate-50"
            >
              <option value="">-- Select --</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">Assigned</label>
            <input
              type="text"
              value={clientFields.assigned || ''}
              onChange={e => handleClientFieldChange('assigned', e.target.value)}
              disabled={!isEditing}
              className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600 disabled:bg-slate-50"
              placeholder="Assigned coach or owner"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Assigned Coach Name
            </label>
            <input
              type="text"
              value={clientFields.assignedCoachName || ''}
              onChange={e => handleClientFieldChange('assignedCoachName', e.target.value)}
              disabled={!isEditing}
              className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600 disabled:bg-slate-50"
              placeholder="Coach name"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Assigned Coach Email
            </label>
            <input
              type="email"
              value={clientFields.assignedCoachEmail || ''}
              onChange={e => handleClientFieldChange('assignedCoachEmail', e.target.value)}
              disabled={!isEditing}
              className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600 disabled:bg-slate-50"
              placeholder="coach@example.com"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Assigned Email (Client)
            </label>
            <input
              type="email"
              value={clientFields.assignedEmail || ''}
              onChange={e => handleClientFieldChange('assignedEmail', e.target.value)}
              disabled={!isEditing}
              className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600 disabled:bg-slate-50"
              placeholder="client.assigned@example.com"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">Cohort Group</label>
            <input
              type="text"
              value={clientFields.cohortGroup || ''}
              onChange={e => handleClientFieldChange('cohortGroup', e.target.value)}
              disabled={!isEditing}
              className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600 disabled:bg-slate-50"
              placeholder="e.g. 1, 2, 3"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
